import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Eye, EyeOff, Cake, ChevronDown, Search, Check, Sparkles, X } from 'lucide-react';
import { useUserStore } from '@/lib/user-store';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { FormLabel, RequiredNote } from '@/components/FormLabel';
import toast from 'react-hot-toast';
import { checkPassword } from '@/lib/password-policy';

const INPUT_CLS = "w-full px-4 py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";

const flagUrl = (iso: string): string => `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;

const COUNTRIES = [
  { code: '+995', country: 'Georgia', iso: 'ge' },
  { code: '+1', country: 'United States', iso: 'us' },
  { code: '+44', country: 'United Kingdom', iso: 'gb' },
  { code: '+49', country: 'Germany', iso: 'de' },
  { code: '+33', country: 'France', iso: 'fr' },
  { code: '+39', country: 'Italy', iso: 'it' },
  { code: '+34', country: 'Spain', iso: 'es' },
  { code: '+90', country: 'Turkey', iso: 'tr' },
  { code: '+7', country: 'Russia', iso: 'ru' },
  { code: '+380', country: 'Ukraine', iso: 'ua' },
  { code: '+48', country: 'Poland', iso: 'pl' },
  { code: '+31', country: 'Netherlands', iso: 'nl' },
  { code: '+41', country: 'Switzerland', iso: 'ch' },
  { code: '+43', country: 'Austria', iso: 'at' },
  { code: '+374', country: 'Armenia', iso: 'am' },
  { code: '+994', country: 'Azerbaijan', iso: 'az' },
  { code: '+81', country: 'Japan', iso: 'jp' },
  { code: '+82', country: 'South Korea', iso: 'kr' },
  { code: '+86', country: 'China', iso: 'cn' },
  { code: '+971', country: 'UAE', iso: 'ae' },
  { code: '+972', country: 'Israel', iso: 'il' },
  { code: '+61', country: 'Australia', iso: 'au' },
  { code: '+55', country: 'Brazil', iso: 'br' },
  { code: '+91', country: 'India', iso: 'in' },
];

const FlagIcon: React.FC<{ iso: string; size?: number }> = ({ iso, size = 20 }) => (
  <img
    src={flagUrl(iso)}
    alt=""
    className="rounded-[3px] object-cover shrink-0"
    style={{ width: size * 1.5, height: size }}
    loading="lazy"
  />
);

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_KA = ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, i) => currentYear - 10 - i);

function getDaysInMonth(month: number, year: number): number {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
}

interface DateDropdownProps {
  value: string;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  onChange: (val: string) => void;
}

const DateDropdown: React.FC<DateDropdownProps> = ({ value, options, placeholder, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium transition-all ${
          open ? 'border-black/20 dark:border-white/20' : ''
        } ${value ? 'text-black dark:text-white' : 'text-black/20 dark:text-white/20'}`}
      >
        <span className="truncate">
          {value ? options.find((o) => o.value === value)?.label || value : placeholder}
        </span>
        <ChevronDown size={14} className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scaleY: 0.95 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -4, scaleY: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 top-full mt-1 left-0 right-0 max-h-48 overflow-y-auto bg-white dark:bg-[#111] border border-black/10 dark:border-white/10 rounded-xl shadow-xl origin-top"
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  opt.value === value
                    ? 'bg-black dark:bg-white text-white dark:text-black font-bold'
                    : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {open && (
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
      )}
    </div>
  );
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const language = (t.nav.home === 'Home') ? 'en' : 'ka';
  const register = useUserStore((s) => s.register);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+995');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryRef = useRef<HTMLDivElement>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');

  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
  const filteredCountries = countrySearch
    ? COUNTRIES.filter((c) => c.country.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.includes(countrySearch))
    : COUNTRIES;

  // Close country dropdown on outside click
  useEffect(() => {
    if (!countryOpen) return;
    const handler = (e: MouseEvent): void => {
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) setCountryOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [countryOpen]);

  const from = (location.state as { from?: string })?.from || '/';

  const handleInput = (field: string, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const months = language === 'ka' ? MONTHS_KA : MONTHS_EN;
  const monthOptions = months.map((label, i) => ({ value: String(i + 1), label }));
  const yearOptions = years.map((y) => ({ value: String(y), label: String(y) }));

  const daysCount = useMemo(
    () => getDaysInMonth(Number(birthMonth), Number(birthYear)),
    [birthMonth, birthYear],
  );
  const dayOptions = Array.from({ length: daysCount }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

  // Reset day if it exceeds month's max days
  if (birthDay && Number(birthDay) > daysCount) {
    setBirthDay('');
  }

  const birthDateISO = birthDay && birthMonth && birthYear
    ? `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`
    : undefined;

  // Calculate age for display
  const age = useMemo(() => {
    if (!birthDateISO) return null;
    const birth = new Date(birthDateISO);
    const today = new Date();
    let a = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) a--;
    return a >= 0 ? a : null;
  }, [birthDateISO]);

  const validate = (): string | null => {
    if (!form.firstName.trim() || !form.lastName.trim()) return 'Name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t.auth.invalidEmail;
    if (!checkPassword(form.password).ok) return t.auth.passwordMinLength;
    if (form.password !== form.confirmPassword) return t.auth.passwordsDontMatch;
    return null;
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    setSubmitting(true);
    const result = await register({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: phoneNumber ? `${countryCode} ${phoneNumber.trim()}` : '',
      birthDate: birthDateISO,
      password: form.password,
    });
    setSubmitting(false);

    if ('user' in result) {
      setWelcomeName(form.firstName.trim());
      setShowWelcome(true);
      return;
    }
    // Error
    if (result.error === 'EMAIL_IN_USE') {
      toast.error(t.auth.emailInUse);
    } else if (result.error === 'RATE_LIMITED') {
      toast.error('Too many registration attempts. Please wait a few minutes.');
    } else if (result.error === 'VALIDATION_ERROR') {
      toast.error(result.message);
    } else {
      toast.error(result.message || 'Registration failed. Please try again.');
    }
  };

  const closeWelcome = (): void => {
    setShowWelcome(false);
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-syncopate font-bold uppercase tracking-tight">
            {t.auth.register}
          </h1>
          <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30">
            {localize(settings?.galleryName) || 'Gallery Fotoatelier'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FormLabel className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2" required>
                {t.auth.firstName}
              </FormLabel>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => handleInput('firstName', e.target.value)}
                className={INPUT_CLS}
                placeholder={t.auth.firstName}
                required
              />
            </div>
            <div>
              <FormLabel className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2" required>
                {t.auth.lastName}
              </FormLabel>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => handleInput('lastName', e.target.value)}
                className={INPUT_CLS}
                placeholder={t.auth.lastName}
                required
              />
            </div>
          </div>

          <div>
            <FormLabel className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2" required>
              {t.auth.email}
            </FormLabel>
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleInput('email', e.target.value)}
              className={INPUT_CLS}
              placeholder="email@example.com"
              required
            />
          </div>

          <div>
            <FormLabel className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2" optional>
              {t.auth.phone}
            </FormLabel>
            <div className="flex gap-2">
              {/* Country code selector */}
              <div className="relative shrink-0" ref={countryRef}>
                <button
                  type="button"
                  onClick={() => { setCountryOpen(!countryOpen); setCountrySearch(''); }}
                  className="flex items-center gap-1.5 px-3 py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium hover:border-black/20 dark:hover:border-white/20 transition-all min-w-[100px]"
                >
                  <FlagIcon iso={selectedCountry.iso} size={16} />
                  <span className="text-black/70 dark:text-white/70">{selectedCountry.code}</span>
                  <ChevronDown size={12} className={`text-black/30 dark:text-white/30 transition-transform ${countryOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {countryOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-50 top-full mt-1 left-0 w-64 bg-white dark:bg-[#111] border border-black/10 dark:border-white/10 rounded-xl shadow-xl overflow-hidden"
                    >
                      <div className="p-2 border-b border-black/5 dark:border-white/5">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-black/[0.03] dark:bg-white/[0.05] rounded-lg">
                          <Search size={13} className="text-black/25 dark:text-white/25 shrink-0" />
                          <input
                            type="text"
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            placeholder={language === 'ka' ? 'ქვეყნის ძიება...' : 'Search country...'}
                            className="w-full text-xs bg-transparent outline-none placeholder:text-black/20 dark:placeholder:text-white/20"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredCountries.map((c) => (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => { setCountryCode(c.code); setCountryOpen(false); }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                              c.code === countryCode
                                ? 'bg-black/[0.05] dark:bg-white/[0.08] font-bold'
                                : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                            }`}
                          >
                            <FlagIcon iso={c.iso} size={16} />
                            <span className="flex-1 text-left text-black/70 dark:text-white/70">{c.country}</span>
                            <span className="text-black/30 dark:text-white/30 text-xs">{c.code}</span>
                            {c.code === countryCode && <Check size={14} className="text-green-500" />}
                          </button>
                        ))}
                        {filteredCountries.length === 0 && (
                          <p className="text-center text-xs text-black/25 dark:text-white/25 py-4">No results</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* Phone number input */}
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s-]/g, ''))}
                className={INPUT_CLS}
                placeholder="XXX XXX XXX"
              />
            </div>
          </div>

          {/* Birthdate — cool dropdown picker */}
          <div>
            <label className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">
              <Cake size={12} />
              {t.auth.birthDate}
              <span className="text-[8px] font-normal tracking-normal normal-case text-black/20 dark:text-white/20">
                ({t.auth.optional})
              </span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <DateDropdown
                value={birthDay}
                options={dayOptions}
                placeholder={t.auth.day}
                onChange={setBirthDay}
              />
              <DateDropdown
                value={birthMonth}
                options={monthOptions}
                placeholder={t.auth.month}
                onChange={setBirthMonth}
              />
              <DateDropdown
                value={birthYear}
                options={yearOptions}
                placeholder={t.auth.year}
                onChange={setBirthYear}
              />
            </div>
            <AnimatePresence>
              {age !== null && age >= 0 && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 text-[10px] font-medium text-black/30 dark:text-white/30 flex items-center gap-1.5"
                >
                  <Cake size={11} className="text-black/20 dark:text-white/20" />
                  {age} {t.auth.yearsOld}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          <div>
            <FormLabel className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2" required>
              {t.auth.password}
            </FormLabel>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => handleInput('password', e.target.value)}
                className={`${INPUT_CLS} pr-12`}
                placeholder="••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20 hover:text-black/50 dark:hover:text-white/50 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="mt-1 text-[9px] text-black/25 dark:text-white/25">{t.auth.passwordMinLength}</p>
          </div>

          <div>
            <FormLabel className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2" required>
              {t.auth.confirmPassword}
            </FormLabel>
            <input
              type={showPassword ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={(e) => handleInput('confirmPassword', e.target.value)}
              className={INPUT_CLS}
              placeholder="••••••"
              required
            />
          </div>

          <RequiredNote />

          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all"
          >
            <UserPlus size={14} />
            {t.auth.registerButton}
          </button>

          <p className="text-center text-[11px] text-black/40 dark:text-white/40">
            {t.auth.hasAccount}{' '}
            <Link
              to="/login"
              state={{ from }}
              className="font-bold text-black dark:text-white hover:underline"
            >
              {t.auth.login}
            </Link>
          </p>
        </form>
      </motion.div>

      {/* Welcome Modal */}
      <AnimatePresence>
        {showWelcome && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[900] bg-black/30 dark:bg-black/50 backdrop-blur-sm"
              onClick={closeWelcome}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 z-[950] flex items-center justify-center px-4 pointer-events-none"
            >
              <div className="bg-white dark:bg-[#111] rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-2xl pointer-events-auto border border-black/5 dark:border-white/5">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 rounded-full bg-black dark:bg-white flex items-center justify-center mx-auto mb-6"
                >
                  <Sparkles size={28} className="text-white dark:text-black" />
                </motion.div>
                <h2 className="text-2xl sm:text-3xl font-syncopate font-bold tracking-tighter text-black dark:text-white mb-2">
                  {localize(settings?.welcomeMessage?.title) || (language === 'ka' ? 'კეთილი იყოს შენი მობრძანება!' : 'Welcome!')}
                </h2>
                <p className="text-black/50 dark:text-white/50 text-sm font-light mb-2">
                  {welcomeName && (language === 'ka' ? `გამარჯობა, ${welcomeName}` : `Hello, ${welcomeName}`)}
                </p>
                <p className="text-black/40 dark:text-white/40 text-xs leading-relaxed mb-8">
                  {localize(settings?.welcomeMessage?.text) || (language === 'ka'
                    ? 'მადლობა რეგისტრაციისთვის! აღმოაჩინეთ საუკეთესო ფოტოხელოვნების ნამუშევრები ჩვენს გალერეაში.'
                    : 'Thank you for joining us! Discover exceptional fine art photography in our gallery.')}
                </p>
                <button
                  onClick={closeWelcome}
                  className="w-full py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-opacity"
                >
                  {language === 'ka' ? 'გაგრძელება' : 'Continue'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterPage;
